<template>
    <!-- Upgrade Required Section -->
    <div v-if="!hasSubsGodTrialAccess" class="mt-[42px]">
        <div class="flex mt-[42px] mb-10 justify-between">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                {{ $t("vue.account_members") }}
            </h1>
        </div>

        <InfoCard
            :img-src="lockedNotAuthorized"
            img-alt="User has no access to content"
            :title="t('vue.upgrade_required')"
            :body-text="t('vue.not_having_access_message')"
            :show-dot="false"
            :disable-top-margin="false"
            :button-label="t('vue.upgrade_my_plan')"
            :button-href="route('billing.subscriptions.index')"
        />
    </div>

    <MemberTabs v-else class="mt-12" tab-key="invitations">
        <div v-if="props.invitations.data.length === 0" class="mt-[42px]">
            <InfoCard
                :img-src="noInvitations"
                img-alt="No pending invitations"
                :title="t('vue.no_pending_invitations')"
                :body-text="t('vue.no_pending_invitations_text')"
                :show-dot="false"
                :disable-top-margin="true"
                :custom-width="119"
                :custom-height="119"
            >
                <template #actions>
                    <AtomicButton
                        v-if="isOwner || isAdmin"
                        color="yellow"
                        size="md"
                        class="h-8 px-4 text-[12px] font-semibold leading-4"
                        @click="
                            checkAccessOrShowModal() && inviteMemberModalShow()
                        "
                    >
                        {{ t("vue.invite_new_members") }}
                    </AtomicButton>
                </template>
            </InfoCard>
        </div>

        <div v-else class="mt-12 pb-80">
            <DynamicContentTable
                v-if="isAdmin || isOwner"
                class="mt-10"
                :title="`${$t('vue.team_invitations')} (${invitationCount})`"
                :subtitle="$t('vue.people_invited_to_your_acc')"
                :search-enabled="false"
                :rows="invitationRows"
                :columns="invitationColumns"
                :endpoint="() => route('members.invitations')"
                :base-query="{}"
                :table="singlePageTable(invitationCount)"
                :pagination="singlePagePagination(invitationCount)"
                empty-text="No results"
                :pagination-enabled="false"
                :per-page-options="[]"
                per-page-label=""
                :row-key="(r: any) => r.token"
            >
                <template #row="{ row: invite }">
                    <!-- Email -->
                    <td class="px-6 py-4 min-w-0">
                        <span
                            class="text-sm font-medium text-gray-700 truncate block"
                        >
                            {{ invite.email }}
                        </span>
                    </td>

                    <!-- Invitation pending -->
                    <td class="px-6 py-4 whitespace-nowrap">
                        <div
                            class="inline-flex items-center rounded-md bg-orange-200 px-2 py-[3px] text-xs font-medium text-gray-700"
                        >
                            {{ $t("vue.invitation_pending") }}
                            ({{ pendingDays(invite.created_at) }}
                            <span>
                                {{
                                    pendingDays(invite.created_at) !== 1
                                        ? $t("vue.days")
                                        : $t("vue.day")
                                }} </span
                            >)
                            <span
                                class="ml-[10px] cursor-pointer text-custom-blue-500 hover:text-custom-blue-600"
                                @click.stop="remindInvitation(invite)"
                            >
                                {{ $t("vue.remind") }}
                            </span>
                        </div>
                    </td>

                    <!-- Role -->
                    <td class="px-6 py-4 whitespace-nowrap">
                        <div
                            class="inline-flex rounded-md bg-blue-200 px-2 py-[3px] text-sm text-gray-800"
                        >
                            {{ invite.role }}
                        </div>
                    </td>

                    <!-- Actions (kebab) -->
                    <td
                        class="pl-2 pr-6 py-4 whitespace-nowrap text-right"
                        @click.stop
                    >
                        <div class="flex justify-end">
                            <button
                                :ref="(el) => setKebabBtn(invite.token, el)"
                                class="kebab-btn"
                                aria-haspopup="menu"
                                :aria-expanded="
                                    menuOpenId === invite.token
                                        ? 'true'
                                        : 'false'
                                "
                                aria-label="Open actions"
                                @click="toggleMenu(invite.token)"
                            >
                                <span
                                    class="kebab-dots"
                                    aria-hidden="true"
                                ></span>
                            </button>

                            <Teleport to="body">
                                <transition name="fade">
                                    <div
                                        v-if="menuOpenId === invite.token"
                                        :ref="
                                            (el) => setMenuEl(invite.token, el)
                                        "
                                        class="menu-popover"
                                        :style="menuStyle"
                                        role="menu"
                                        @keydown.esc.stop.prevent="closeMenu"
                                    >
                                        <!-- Delete -->
                                        <button
                                            class="menu-item danger"
                                            role="menuitem"
                                            @click="onDeleteInvite(invite)"
                                        >
                                            Delete
                                        </button>

                                        <!-- other actions later -->
                                        <!--
                                        <button class="menu-item" role="menuitem">Settings</button>
                                        -->
                                    </div>
                                </transition>
                            </Teleport>
                        </div>
                    </td>
                </template>
            </DynamicContentTable>
        </div>
    </MemberTabs>

    <InviteMemberModal
        v-if="inviteMemberModal"
        @close="inviteMemberModal = false"
    />
    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Inviting team members requires a Scale plan subscription. Upgrade your subscription to access team management features."
        @close="closeUpgradeModal"
    />
</template>

<script lang="ts" setup>
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IInvitation from "@/types/Members/IInvitation";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { TrashIcon } from "@heroicons/vue/24/outline";
import usePermissions from "@/composables/usePermissions";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { usePage } from "@inertiajs/vue3";
import { differenceInCalendarDays } from "date-fns";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import { useTranslations } from "@/composables/useTranslations";
import useAlertsAccess from "@/composables/useAlertsAccess.ts";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";
import useTeamMemberAccess from "@/composables/useTeamMemberAccess.ts";
// import { ref } from "vue";
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";

import useSubsGodTrialAccess from "@/composables/useSubsGodTrialAccess.ts";

const props = defineProps<{
    invitations: IPaginationData<IInvitation>;
}>();

const { hasSubsGodTrialAccess } = useSubsGodTrialAccess();

// Get access to translations
const { t } = useTranslations();

const { isAdmin, isOwner } = usePermissions();

const { showUpgradeModal, checkAccessOrShowModal, closeUpgradeModal } =
    useTeamMemberAccess();

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

const headersInvitations: IDataTableHeader[] = [
    { text: t("vue.email"), id: "email" },
    { text: t("vue.team"), id: "token" },
    {
        text: t("vue.invitation_pending"),
        id: "created_at",
    },
    {
        text: t("vue.permissions_role"),
        id: "role",
        align: "left",
    },
    { text: "", id: "custom" },
];

const remindInvitation = (value: IInvitation) => {
    const form = {
        email: value.email,
        role: value.role,
    };
    router.post(route("members.store"), form, {
        preserveScroll: true,
        onSuccess: () => {
            create({
                title: t("vue.invitation_reminded"),
                type: "info",
            });
        },
        onError: () => {
            create({
                title: t("vue.invitation_not_reminded"),
                text: t("vue.error_reminding_invitation"),
                type: "error",
            });
        },
    });
};

const deleteInvitation = (value: IInvitation) => {
    router.get(
        route("team.invitation.refuse", { token: value.token }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: t("vue.invitation_deleted"),
                    type: "info",
                });
            },
            onError: () => {
                create({
                    title: t("vue.invitation_not_deleted"),
                    text: t("vue.error_deleting_invitation"),
                    type: "error",
                });
            },
        },
    );
};

const inviteMemberModal = ref(false);
const inviteMemberModalErrors = ref<IErrors>();

const inviteMemberModalShow = () => {
    inviteMemberModalErrors.value = undefined;
    inviteMemberModal.value = true;
};

const noInvitations = new URL(
    "/resources/js/assets/images/city-empty.svg",
    import.meta.url,
).href;

const lockedNotAuthorized = new URL(
    "/resources/js/assets/images/locked-not-authorized.svg",
    import.meta.url,
).href;

/*TABLE START*/
const invitationRows = computed(() => props.invitations.data);
const invitationCount = computed(() => props.invitations.data.length);

const pendingDays = (createdAt: string) =>
    differenceInCalendarDays(new Date(), new Date(createdAt));

type SortDir = "asc" | "desc";

const invitationColumns = [
    {
        key: "email",
        label: t("vue.email"),
        colClass: "w-[38%]",
        sortable: false,
    },
    {
        key: "pending",
        label: t("vue.invitation_pending"),
        colClass: "w-[34%]",
        sortable: false,
    },
    {
        key: "role",
        label: t("vue.permissions_role"),
        colClass: "w-[20%]",
        sortable: false,
    },
    { key: "actions", label: "", colClass: "w-[72px]", sortable: false },
];

const singlePageTable = (total: number) => ({
    sort_by: null,
    sort_dir: null as SortDir | null,
    per_page: total || 1,
    current_page: 1,
    q: "",
});

const singlePagePagination = (total: number) => ({
    total,
    per_page: total || 1,
    current_page: 1,
    last_page: 1,
    from: total ? 1 : null,
    to: total ? total : null,
});

/*TABLE END*/

/*KEBAB MENU START*/
const menuOpenId = ref<string | number | null>(null);

const kebabBtns = ref<Record<string, HTMLElement | null>>({});
const menus = ref<Record<string, HTMLElement | null>>({});

const setKebabBtn = (id: string | number, el: HTMLElement | null) => {
    kebabBtns.value[String(id)] = el;
};

const setMenuEl = (id: string | number, el: HTMLElement | null) => {
    menus.value[String(id)] = el;
};

const menuPos = ref({ top: 0, left: 0 });

const updateMenuPosition = () => {
    if (!menuOpenId.value) return;

    const id = String(menuOpenId.value);
    const btn = kebabBtns.value[id];
    const menu = menus.value[id];
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const padding = 8;

    const menuW = menu?.offsetWidth ?? 176;
    const menuH = menu?.offsetHeight ?? 0;

    let top = rect.bottom + 8;
    let left = rect.right - menuW;

    left = Math.min(
        Math.max(left, padding),
        window.innerWidth - menuW - padding,
    );

    if (menuH && top + menuH > window.innerHeight - padding) {
        top = Math.max(padding, rect.top - menuH - 8);
    }

    menuPos.value = { top, left };
};

const menuStyle = computed(() => ({
    position: "fixed",
    top: `${menuPos.value.top}px`,
    left: `${menuPos.value.left}px`,
    zIndex: "99999",
}));

const toggleMenu = async (id: string | number) => {
    menuOpenId.value = menuOpenId.value === id ? null : id;

    if (menuOpenId.value) {
        await nextTick();
        requestAnimationFrame(() => updateMenuPosition());
    }
};

const closeMenu = () => {
    menuOpenId.value = null;
};

const onClickOutside = (e: MouseEvent) => {
    if (!menuOpenId.value) return;

    const id = String(menuOpenId.value);
    const menu = menus.value[id];
    const btn = kebabBtns.value[id];
    const target = e.target as Node;

    if (menu && !menu.contains(target) && btn && !btn.contains(target)) {
        closeMenu();
    }
};

const onScrollOrResize = () => {
    if (!menuOpenId.value) return;
    updateMenuPosition();
};

onMounted(() => {
    document.addEventListener("click", onClickOutside);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", onClickOutside);
    window.removeEventListener("resize", onScrollOrResize);
    window.removeEventListener("scroll", onScrollOrResize, true);
});

const onDeleteInvite = (invite: IInvitation) => {
    closeMenu();
    deleteInvitation(invite);
};
/*KEBAB MENU END*/
</script>
<style scoped>
.kebab-btn {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    border-radius: 8px;
}
.kebab-btn:hover {
    background: #f3f4f6;
}

.kebab-dots {
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
    position: relative;
}
.kebab-dots::before,
.kebab-dots::after {
    content: "";
    position: absolute;
    left: 0;
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: #6b7280;
}
.kebab-dots::before {
    top: -7px;
}
.kebab-dots::after {
    top: 7px;
}

.menu-popover {
    z-index: 99999;
    width: 176px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 8px 24px rgba(16, 24, 40, 0.12),
        0 2px 8px rgba(16, 24, 40, 0.06);
    padding: 6px 0;
}

.menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 20px;
    color: #374151;
}
.menu-item:hover {
    background: #f9fafb;
}

.menu-item.danger {
    color: #b91c1c;
}
.menu-item.danger:hover {
    background: #fef2f2;
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.12s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
